import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { API_BASE_URL } from '../../config';
import axios from 'axios';
import { FormControlLabel, Switch } from '@mui/material';
import Card from '@/components/Card/Card';
import CardBody from '@/components/Card/CardBody';
import CardHeader from '@/components/Card/CardHeader';
import Button from '@/components/CustomButtons/Button';
import CustomInput from '@/components/CustomInput/CustomInput';
import GridContainer from '@/components/Grid/GridContainer';
import GridItem from '@/components/Grid/GridItem';
import loader from '../../assets/img/loader.svg';
import OrganizationFeatures from './OrganizationFeatures';

const CreateOrganization = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [loaded, setLoaded] = useState(false);
  const [name, setName] = useState('');
  const [giftcards, setGiftCards] = useState('');
  const [donations, setDonations] = useState('');
  const [venmo, setVenmo] = useState('');
  const [paypal, setPaypal] = useState('');
  const [automatedAchReturnEnabled, setAutomatedAchReturnEnabled] = useState(
    false,
  );
  const [message, setMessage] = useState('');
  const [features, setFeatures] = useState({
    showApprovalsPage: false,
    showCreatePaymentPage: false,
    showBusinessUnitsPage: false,
    showFacilityManagementPage: false,
    showSupplierLookupModal: false,
    showBulkApproveDecline: true,
    enforceClientTransactionIdUniqueness: false,
    disableLegacyEmailTemplates: false,
    clientWebsiteUrl: '',
    clientWebsiteLabel: '',
  });

  const handleName = (e) => {
    setName(e.target.value);
  };

  const handleGiftCards = (e) => setGiftCards(e.target.value);
  const handleDonations = (event) => setDonations(event.target.value);
  const handleVenmo = (e) => setVenmo(e.target.value);
  const handlePaypal = (e) => setPaypal(e.target.value);
  const handleAutomatedAchReturnEnabled = (e) =>
    setAutomatedAchReturnEnabled(e.target.checked);

  const cancelfrm = () => {
    navigate('/admin/organizations');
  };

  const createOrganization = async (e) => {
    e.preventDefault();
    if (name === '') {
      alert(t('organizations.create.validation.nameRequired'));
      return false;
    } else {
      setLoaded(true);
      const formData = new FormData();
      formData.append('name', name);
      formData.append('giftcards', giftcards);
      formData.append('donations', donations);
      formData.append('venmo', venmo);
      formData.append('paypal', paypal);
      formData.append(
        'automated_ach_return_enabled',
        automatedAchReturnEnabled,
      );
      formData.append('features', JSON.stringify(features));
      const headers = {
        'Content-Type': 'multipart/form-data',
      };

      axios
        .post(API_BASE_URL + '/admin/addOrganization', formData, {
          headers: headers,
        })
        .then((response) => {
          return response;
        })
        .then((res) => {
          if (!res.data?.err) {
            if (res.data?.id) {
              navigate(`/admin/updateOrganization/${res.data?.id}`);
            } else {
              navigate('/admin/organizations');
            }

            setLoaded(false);
          } else {
            setMessage(res.data?.msg);
            setLoaded(false);
          }
        })
        .catch((err) => {
          setMessage(err.response?.data?.msg);
          setLoaded(false);
        });
    }
  };

  return (
    <div>
      <form onSubmit={createOrganization}>
        <GridContainer>
          <GridItem xs={12} sm={12} md={12}>
            <Card>
              <CardHeader color='primary'>
                <h4>{t('organizations.create.title')}</h4>
              </CardHeader>
              <CardBody>
                {loaded && (
                  <div className='loaders'>
                    <img src={loader} />
                  </div>
                )}
                <GridContainer>
                  <GridItem xs={12} sm={12} md={6}>
                    <CustomInput
                      type='text'
                      labelText={t('organizations.create.nameLabel')}
                      onChange={handleName}
                      value={name}
                      id='name'
                      name='name'
                      formControlProps={{
                        fullWidth: true,
                      }}
                    />
                  </GridItem>
                </GridContainer>
                <GridContainer>
                  <GridItem xs={12} sm={12} md={6}>
                    <CustomInput
                      labelText={t('organizations.create.giftcardsLabel')}
                      id='giftcards'
                      name='giftcards'
                      onChange={handleGiftCards}
                      value={giftcards}
                      type='text'
                      formControlProps={{
                        fullWidth: true,
                      }}
                    />
                  </GridItem>
                  <GridItem xs={12} sm={12} md={6}>
                    <CustomInput
                      labelText={t('organizations.create.donationsLabel')}
                      id='donations'
                      name='donations'
                      onChange={handleDonations}
                      value={donations}
                      type='text'
                      formControlProps={{
                        fullWidth: true,
                      }}
                    />
                  </GridItem>
                </GridContainer>

                <GridContainer>
                  <GridItem xs={12} sm={12} md={6}>
                    <CustomInput
                      labelText={t('organizations.create.venmoLabel')}
                      id='venmo'
                      name='venmo'
                      onChange={handleVenmo}
                      value={venmo}
                      type='text'
                      formControlProps={{
                        fullWidth: true,
                      }}
                    />
                  </GridItem>
                  <GridItem xs={12} sm={12} md={6}>
                    <CustomInput
                      labelText={t('organizations.create.paypalLabel')}
                      id='paypal'
                      name='paypal'
                      onChange={handlePaypal}
                      value={paypal}
                      type='text'
                      formControlProps={{
                        fullWidth: true,
                      }}
                    />
                  </GridItem>
                </GridContainer>

                <GridContainer>
                  <GridItem xs={12} sm={12} md={12}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={automatedAchReturnEnabled}
                          onChange={handleAutomatedAchReturnEnabled}
                          name='automated_ach_return_enabled'
                          color='primary'
                        />
                      }
                      label={t('organizations.create.achReturnLabel')}
                      sx={{
                        my: 2,
                        '& .MuiFormControlLabel-label': { fontSize: '0.9rem' },
                      }}
                    />
                  </GridItem>
                </GridContainer>

                <OrganizationFeatures
                  features={features}
                  setFeatures={setFeatures}
                />

                {message && (
                  <GridContainer>
                    <GridItem xs={8} sm={8} md={6}>
                      <p style={{ color: 'red', padding: '10px 0' }}>
                        {message}
                      </p>
                    </GridItem>
                  </GridContainer>
                )}
                <GridContainer>
                  <GridItem xs={12} sm={12} md={6}>
                    <Button color='primary' type='submit'>
                      {t('common.buttons.create')}
                    </Button>
                    <Button
                      color='primary'
                      onClick={cancelfrm}
                      className='btn-cancel'
                      style={{ marginLeft: '5px' }}>
                      {t('common.buttons.cancel')}
                    </Button>
                  </GridItem>
                  <GridItem xs={12} sm={12} md={6}></GridItem>
                </GridContainer>
              </CardBody>
            </Card>
          </GridItem>
        </GridContainer>
      </form>
    </div>
  );
};

export default CreateOrganization;
